import { FC, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useSearchParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { toast } from 'react-hot-toast'
import { appEventInstance } from '../utils/event'
import { currentTeamState, currentUserState } from '../utils/state-atoms'
import { CurrentUserKey, GMMSRaceTeamKey } from '../utils/constants'
import { useGetUserLazy } from '../api/user-API'
import { protoService } from '../proto/ProtoService'
import { queryString2JsonString } from '../types/util'
import ProtectedRoutes from '../routing/ProtectedRoutes'
import ErrorBoundary from './ErrorBoundary'
import Footer from './Footer'
import Navbar from './Navbar'

const MainLayout: FC = () => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const currentTeam = useRecoilValue(currentTeamState)
  const currentUser = useRecoilValue(currentUserState)
  const [, fetchUser] = useGetUserLazy()
  const attemptedTeamSwitch = useRef<string | null>(null)
  const [isTeamSwitching, setIsTeamSwitching] = useState(false)

  // Detect malformed team-swap URLs where `&raceTeamId=xxx` was appended to the
  // hash path without a leading `?`.  Example:
  //   BAD:  #/submissions/26_RC035_R003&raceTeamId=apg-f1
  //   GOOD: #/submissions/26_RC035_R003?raceTeamId=apg-f1
  // When `&` is used, React Router bakes `raceTeamId=apg-f1` into params.id
  // instead of searchParams, so the team-swap logic never fires and the API is
  // called with a corrupted run ID.  We redirect to the canonical form so that
  // both the team-swap and the page fetch work correctly.
  useEffect(() => {
    const hash = window.location.hash // e.g. "#/submissions/26_RC035_R003&raceTeamId=apg-f1"
    // Only act when there is NO `?` already in the hash (truly malformed)
    if (!hash.includes('?')) {
      const ampMatch = hash.match(/&raceTeamId=([^&]+)/)
      if (ampMatch) {
        const teamId = ampMatch[1]
        const cleanHash = hash.replace(`&raceTeamId=${teamId}`, '')
        const correctedHash = `${cleanHash}?raceTeamId=${teamId}`
        // Replace the URL without adding a history entry so Back still works
        window.location.replace(window.location.origin + window.location.pathname + correctedHash)
      }
    }
  }, [])

  useEffect(() => {
    // Listen for team data loaded event
    const handleTeamDataLoaded = () => {
      console.log('[MainLayout] Team data loaded, clearing switching state')
      setIsTeamSwitching(false)
      // Reset attemptedTeamSwitch after successful swap so future navigations work
      attemptedTeamSwitch.current = null
    }

    appEventInstance.addEventListener('teamDataLoaded', handleTeamDataLoaded)

    return () => {
      appEventInstance.removeEventListener('teamDataLoaded', handleTeamDataLoaded)
    }
  }, [])

  useEffect(() => {
    // Check if there's a raceTeamId in the URL
    const raceTeamId = searchParams.get('raceTeamId')

    if (raceTeamId && currentUser) {
      // Only change team if it's different from the current team in localStorage
      // (currentTeam from Recoil might not be updated yet)
      const currentTeamInStorage = localStorage.getItem(GMMSRaceTeamKey)
      //attemptedTeamSwitch is used to prevent infinite loop of team switching if the URL still has the raceTeamId after switching
      if (raceTeamId !== currentTeamInStorage && attemptedTeamSwitch.current !== raceTeamId) {
        console.log('Detected raceTeamId in URL, validating and switching team to:', raceTeamId)
        console.log('Current team in localStorage:', currentTeamInStorage)
        attemptedTeamSwitch.current = raceTeamId
        setIsTeamSwitching(true)

        // Validate that the user has an account on this team
        const validateAndSwitchTeam = async () => {
          const user = JSON.parse(localStorage.getItem(CurrentUserKey))

          // Try fetching by OID first
          let userData = await fetchUser(
            'query=' +
              queryString2JsonString(
                `cloudProviderLDAPRef=${user.cloudProviderLDAPRef}&raceTeamId=${raceTeamId}`
              )
          ).catch(() => ({ data: null }))

          // If not found by OID, try by email (for records not yet migrated)
          if (!userData.data || !protoService.decodeProtoResponse(userData.data, 'user')[0]) {
            userData = await fetchUser(
              'query=' + queryString2JsonString(`email=${user.email}&raceTeamId=${raceTeamId}`)
            ).catch(() => ({ data: null }))
          }

          if (userData.data) {
            const fetchedUser = protoService.decodeProtoResponse(userData.data, 'user')
            if (!fetchedUser[0]) {
              toast.error(
                `Cannot switch to race team ${raceTeamId}: your account hasn't been set up, please contact the CFD team.`
              )
              return
            }

            // User is valid for this team, proceed with team switch
            localStorage.setItem(GMMSRaceTeamKey, raceTeamId)
            // Skip auto-navigation when switching via URL parameter to preserve the intended destination
            console.log('[MainLayout] Calling teamChanged with skipNavigation=true')
            appEventInstance.teamChanged(true)
            console.log('[MainLayout] teamChanged called')
          } else {
            toast.error(
              `Cannot switch to race team ${raceTeamId}: your account hasn't been set up, please contact the CFD team.`
            )
            setIsTeamSwitching(false)
          }
        }

        validateAndSwitchTeam()
      }
    }
  }, [searchParams, currentUser, fetchUser])

  return (
    <ProtectedRoutes>
      <div className="min-h-[100vh] flex flex-col items-stretch dark:bg-neutral-90 dark:text-neutral-10">
        <Navbar />
        <div className="px-8 w-full flex flex-col items-center basis-0 grow">
          {isTeamSwitching ? (
            <div className="w-full h-96 flex flex-col justify-center items-center space-y-4">
              <div className="text-lg font-medium text-gray-700 dark:text-neutral-10">
                Switching team...
              </div>
            </div>
          ) : (
            <ErrorBoundary resetKey={location.pathname}>
              <Outlet key={currentTeam} />
            </ErrorBoundary>
          )}
        </div>
        <Footer />
      </div>
    </ProtectedRoutes>
  )
}

export default MainLayout
